import { startRegistration } from '@simplewebauthn/browser';
import { useEffect, useState } from 'react';

import {
  getPasskeyRegisterOptions,
  listAccountSessions,
  updateProfile,
  updatePassword,
  verifyPasskeyRegister,
  type AccountSession,
} from '../lib/api';
import { useAuth } from '../hooks/use-auth';

export function AccountPage() {
  const { user, refreshSession } = useAuth();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<AccountSession[]>([]);
  const [registeringPasskey, setRegisteringPasskey] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    void loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDisplayName(user?.display_name ?? '');
  }, [user?.display_name]);

  const loadSessions = async () => {
    setError(null);
    setLoadingSessions(true);
    try {
      const data = await listAccountSessions();
      setSessions(data);
    } catch (err) {
      setSessions([]);
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setLoadingSessions(false);
    }
  };

  const submitPasswordChange = async () => {
    setError(null);
    setMessage(null);

    if (!newPassword) {
      setError('New password is required');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation must match');
      return;
    }

    setChangingPassword(true);
    try {
      await updatePassword({
        new_password: newPassword,
      });
      setNewPassword('');
      setConfirmPassword('');
      setMessage('Password updated successfully');
      await loadSessions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password update failed');
    } finally {
      setChangingPassword(false);
    }
  };

  const submitProfileUpdate = async () => {
    setError(null);
    setMessage(null);

    const trimmed = displayName.trim();
    if (!trimmed) {
      setError('Display name cannot be empty');
      return;
    }
    if (trimmed.length > 80) {
      setError('Display name is too long (max 80 chars)');
      return;
    }

    setUpdatingProfile(true);
    try {
      await updateProfile({ display_name: trimmed });
      await refreshSession();
      setMessage('Profile updated successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Profile update failed');
    } finally {
      setUpdatingProfile(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Account</h1>
        <p className="mt-1 text-sm text-slate-600">
          Manage sign-in methods, password security, and active sessions.
        </p>
      </div>

      {message ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {message}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Profile</h2>
          <div className="mt-3">
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Display name</span>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={displayName}
                  onChange={(event) => {
                    setDisplayName(event.target.value);
                  }}
                  maxLength={80}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
                />
                <button
                  type="button"
                  disabled={updatingProfile}
                  className="rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => {
                    void submitProfileUpdate();
                  }}
                >
                  {updatingProfile ? 'Saving...' : 'Save'}
                </button>
              </div>
            </label>
          </div>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 bg-white px-3 py-2">
              <dt className="text-slate-500">Display name</dt>
              <dd className="font-medium text-slate-900">{user?.display_name ?? '-'}</dd>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 bg-white px-3 py-2">
              <dt className="text-slate-500">Email</dt>
              <dd className="font-medium text-slate-900">{user?.email ?? '-'}</dd>
            </div>
          </dl>
        </section>

        <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Passkey</h2>
          <p className="mt-2 text-sm text-slate-600">
            Use a passkey for faster, phishing-resistant sign-in.
          </p>
          <button
            type="button"
            disabled={registeringPasskey}
            className="mt-4 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            onClick={async () => {
              setError(null);
              setMessage(null);
              setRegisteringPasskey(true);
              try {
                const optionsResponse = await getPasskeyRegisterOptions();
                const registration = await startRegistration({
                  optionsJSON: optionsResponse.options as Parameters<
                    typeof startRegistration
                  >[0]['optionsJSON'],
                });
                await verifyPasskeyRegister({
                  sessionToken: optionsResponse.sessionToken,
                  response: registration,
                });
                await refreshSession();
                setMessage('Passkey registered successfully');
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Passkey registration failed');
              } finally {
                setRegisteringPasskey(false);
              }
            }}
          >
            {registeringPasskey ? 'Registering passkey...' : 'Register new passkey'}
          </button>
        </section>
      </div>

      <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
              Password
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Reset password while logged in. Use at least 8 characters.
            </p>
          </div>
          <button
            type="button"
            disabled={changingPassword}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => {
              void submitPasswordChange();
            }}
          >
            {changingPassword ? 'Updating password...' : 'Update password'}
          </button>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-slate-600">New password</span>
            <input
              type="password"
              value={newPassword}
              onChange={(event) => {
                setNewPassword(event.target.value);
              }}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
            />
          </label>
          <label className="text-sm md:col-span-1">
            <span className="mb-1 block text-slate-600">Confirm new password</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
              }}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
            />
          </label>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
              Active sessions
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Review currently active login sessions for this account.
            </p>
          </div>
          <button
            type="button"
            disabled={loadingSessions}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => {
              void loadSessions();
            }}
          >
            {loadingSessions ? 'Refreshing...' : 'Refresh sessions'}
          </button>
        </div>

        {sessions.length > 0 ? (
          <ul className="mt-4 space-y-3 text-sm">
            {sessions.map((session) => (
              <li key={session.id} className="rounded-md border border-slate-200 bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium text-slate-900">
                    {formatSessionMethod(session.login_method)}
                  </p>
                  {session.current ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                      Current session
                    </span>
                  ) : null}
                </div>
                <div className="mt-2 grid gap-1 text-xs text-slate-600 md:grid-cols-2">
                  <p>Session ID: {session.id}</p>
                  <p>IP: {session.ip_address || '-'}</p>
                  <p>Created: {formatTimestamp(session.created_at)}</p>
                  <p>Expires: {formatTimestamp(session.expires_at)}</p>
                  <p className="md:col-span-2">User agent: {session.user_agent || '-'}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 rounded-md border border-slate-200 bg-white px-3 py-3 text-sm text-slate-600">
            No active sessions found.
          </p>
        )}
      </section>
    </div>
  );
}

function formatSessionMethod(method: string): string {
  if (method === 'passkey') {
    return 'Passkey login';
  }
  if (method === 'password') {
    return 'Password login';
  }
  return method;
}

function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toISOString().slice(0, 19).replace('T', ' ');
}
